import React, { useState, useEffect } from 'react';
import { TextPassage, ProgressBar, Alert, AlertVariantType, Button } from '../common';
import { Grid } from '../common/layouts/Grid';
import StandardModal from '../common/modals/StandardModal';
import { TextArea } from '@emorg-prd/standard-react';
import { Params, useLocation, useNavigate, useParams } from 'react-router-dom';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import './ApprovalsHome.css';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import ApprovalsHomeFilters from './ApprovalsHomeFilters';
import ApprovalsAccountsTable from './ApprovalsAccountsTable';
import CloudAccount from '../../models/CloudAccount';
import CloudAccountsService from '../../services/CloudAccountsService';
import moment from 'moment';
import ApprovalsEntraIDAppsTable from './ApprovalsEntraIDAppsTable';
import ApplicationsService from '../../services/ApplicationsService';
import EnterpriseApp from '../../models/entities/EnterpriseApp';
import TAP from '../../models/entities/TAP';
import TemporaryAccessPasswordService from '../../services/TemporaryAccessPasswordService';
import ApprovalsTAPTable from './ApprovalsTAPTable';
import PortOpeningRequest from '../../models/entities/PortOpeningRequest';
import PortOpeningRequestService from '../../services/PortOpeningRequestService';
import ApprovalsPORsTable from './ApprovalsPORsTable';
import DoNotDecryptRequest from '../../models/entities/DoNotDecryptRequest';
import DoNotDecryptRequestService from '../../services/DoNotDecryptRequestService';
import ApprovalsDPIsTable from './ApprovalsDPIsTable';
import AWSPortOpeningRequest from '../../models/entities/AWSPortOpeningRequests';
import AWSPortOpeningRequestsService from '../../services/AWSPortOpeningRequestsService';
import ApprovalsAWSPORsTable from './ApprovalsAWSPORsTable';
import OnPremPortOpeningRequest from '../../models/entities/PortOpeningRequestOnPrem';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import ApprovalsOnPremPORsTable from './ApprovalsOnPremPORsTable';

const memoized = {
	myCloudAccounts: void 0,
	myApps: void 0,
	myPors: void 0,
	myDpis: void 0,
	myTaps: void 0,
	myAWSPORs: void 0,
	myOnprempors: void 0,
};

const ApprovalsHome = () => {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const tab = params.get('tab');
	const [areMyApprovalsLoading, setAreMyApprovalsLoading] = useState<boolean>(true);
	const [progressLabel, setProgressLabel] = useState<string>('Loading your approvals...');
	const [activeTab, setActiveTab] = useState<string>(tab);
	const [endModal, setEndModal] = useState<AlertBanner>({ visible: false });
	const [cloudAccounts, setCloudAccounts] = useState<CloudAccount[]>([]);
	const [filteredAccounts, setFilteredAccounts] = useState<CloudAccount[]>([]);
	const [PORs, setPORs] = useState<PortOpeningRequest[]>([]);
	const [filteredPORs, setFilteredPORs] = useState<PortOpeningRequest[]>([]);
	const [DPIs, setDPIs] = useState<DoNotDecryptRequest[]>([]);
	const [filteredDPIs, setFilteredDPIs] = useState<DoNotDecryptRequest[]>([]);
	const [enterpriseApps, setEnterpriseApps] = useState<EnterpriseApp[]>([]);
	const [TAPs, setTAPs] = useState<TAP[]>([]);
	const [filteredTAPs, setFilteredTAPs] = useState<TAP[]>([]);
	const [OnPremPORs, setOnPremPORs] = useState<OnPremPortOpeningRequest[]>([]);
	const [filteredOnPremPORs, setFilteredOnPremPORs] = useState<OnPremPortOpeningRequest[]>([]);
	const [filteredEnterpriseApps, setFilteredEnterpriseApps] = useState<EnterpriseApp[]>([]);
	const [AWSPORs, setAWSPORs] = useState<AWSPortOpeningRequest[]>([]);
	const [filteredAWSPORs, setFilteredAWSPORs] = useState<AWSPortOpeningRequest[]>([]);
	const [selectedCloudAccount, setSelectedCloudAccount] = useState<CloudAccount>(null);
	const [approveModalVisible, setApproveModalVisible] = useState<boolean>(false);
	const [rejectModalVisible, setRejectModalVisible] = useState<boolean>(false);
	const [reasonModalVisible, setReasonModalVisible] = useState<boolean>(false);
	const [rejectionReason, setRejectionReason] = useState<string>('');
	const [refresh, setRefresh] = useState<boolean>(false);
	const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({});

	const [alert, setAlertBanner] = useState<AlertBanner>({
		visible: false,
		variant: 'error',
		text: '',
	});
	const cloudAccountsService = new CloudAccountsService();
	const applicationsService = new ApplicationsService();
	const temporaryAccessPasswordService = new TemporaryAccessPasswordService();
	const portOpeningRequestService = new PortOpeningRequestService();
	const doNotDecryptRequestService = new DoNotDecryptRequestService();
	const awsPortOpeningRequestsService = new AWSPortOpeningRequestsService();
	const onPremPortOpeningRequestService = new OnPremPortOpeningRequestService();
	function refreshCache() {
		memoized.myCloudAccounts = void 0;
		memoized.myApps = void 0;
		memoized.myPors = void 0;
		memoized.myDpis = void 0;
		memoized.myTaps = void 0;
		memoized.myAWSPORs = void 0;
		memoized.myOnprempors = void 0;
	}
	let timeoutId = null;
	const navigate = useNavigate();
	const retrieveApprovals = async (isRefresh: boolean) => {
		setAreMyApprovalsLoading(true);
		setProgressLabel(`${isRefresh ? 'Reloading' : 'Loading'} your approvals...`);

		if (isRefresh) refreshCache();

		type FetchEntry = {
			key: string;
			fetch: () => Promise<any>;
			setter: (data: any) => void;
			filteredSetter: (data: any) => void;
		};

		const allFetches: FetchEntry[] = [
			{ key: 'accounts',      fetch: getAccountsForApprove,       setter: setCloudAccounts,    filteredSetter: setFilteredAccounts },
			{ key: 'apps',          fetch: getMyAppsForApprove,          setter: setEnterpriseApps,   filteredSetter: setFilteredEnterpriseApps },
			{ key: 'pors',          fetch: getMyPORsForApprove,          setter: setPORs,             filteredSetter: setFilteredPORs },
			{ key: 'dpis',          fetch: getMyDPIsForApprove,          setter: setDPIs,             filteredSetter: setFilteredDPIs },
			{ key: 'taps',          fetch: getMyTapsForApprove,          setter: setTAPs,             filteredSetter: setFilteredTAPs },
			{ key: 'awspors',       fetch: getMyAWSPORsForApprove,       setter: setAWSPORs,          filteredSetter: setFilteredAWSPORs },
			{ key: 'onprempors',    fetch: getMyOnPremPORsForApprove,    setter: setOnPremPORs,       filteredSetter: setFilteredOnPremPORs },
		];

		// Mark all tabs as loading
		setLoadingTabs(Object.fromEntries(allFetches.map(e => [e.key, true])));

		const priority = allFetches.find(e => e.key === activeTab);
		const rest = allFetches.filter(e => e.key !== activeTab);

		try {
			//Fetch the selected tab first, update both raw and filtered state, then show UI
			if (priority) {
				const data = await priority.fetch();
				priority.setter(data);
				priority.filteredSetter(data);
				setLoadingTabs(prev => ({ ...prev, [priority.key]: false }));
			}
			setAreMyApprovalsLoading(false);
			setAlertBanner({ visible: false });

			// Fetch all remaining tabs in parallel in the background, updating both states as each resolves
			await Promise.allSettled(
				rest.map(e =>
					e.fetch().then(data => {
						e.setter(data);
						e.filteredSetter(data);
						setLoadingTabs(prev => ({ ...prev, [e.key]: false }));
					})
				)
			);
		} catch (err) {
			handleEndModal('Approvals could not be retrieved.', 'error', 10000);
		}
	};
	const getAccountsForApprove = async () => {
		if (typeof memoized.myCloudAccounts !== 'undefined') return memoized.myCloudAccounts;
		let data = [];
		data = (await cloudAccountsService.getAccountsForApprove()).reverse();
		memoized.myCloudAccounts = data;
		return data;
	};
	const getMyAppsForApprove = async () => {
		if (typeof memoized.myApps !== 'undefined') return memoized.myApps;
		let data = [];
		data = (await applicationsService.getMyAppsApprovals()).reverse();
		memoized.myApps = data;
		return data;
	};
	const getMyPORsForApprove = async () => {
		if (typeof memoized.myPors !== 'undefined') return memoized.myPors;
		let data = [];
		data = (await portOpeningRequestService.getMyApprovals()).reverse();
		memoized.myPors = data;
		return data;
	};
	const getMyDPIsForApprove = async () => {
		if (typeof memoized.myDpis !== 'undefined') return memoized.myDpis;
		let data = [];
		data = (await doNotDecryptRequestService.getMyApprovals()).reverse();
		memoized.myDpis = data;
		return data;
	};
	const getMyTapsForApprove = async () => {
		if (typeof memoized.myTaps !== 'undefined') return memoized.myTaps;
		let data = [];
		data = (await temporaryAccessPasswordService.getAll()).approvals
			.filter((tap) => tap.approvalStatus === 'Requested')
			.reverse();
		memoized.myTaps = data;
		return data;
	};
	const getMyAWSPORsForApprove = async () => {
		if (typeof memoized.myAWSPORs !== 'undefined') return memoized.myAWSPORs;
		let data = [];
		data = (await awsPortOpeningRequestsService.getMyApprovals()).reverse();
		memoized.myAWSPORs = data;
		return data;
	};
	const getMyOnPremPORsForApprove = async () => {
		if (typeof memoized.myOnprempors !== 'undefined') return memoized.myOnprempors;
		let data = [];
		data = (await onPremPortOpeningRequestService.getMyApprovals()).reverse();
		memoized.myOnprempors = data;
		return data;
	};
	const handleRequestApproved = async () => {
		setApproveModalVisible(false);
		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver
				? await cloudAccountsService.approveUserCloudAccount(cloudAccount.id)
				: await cloudAccountsService.approveCloudAccount(cloudAccount.id);

			handleAccountChange(cloudAccount, updatedAccount);

			handleSuccessAlert(
				`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been approved.`
			);
		} catch (error) {
			handleError(error);
		}
	};
	const handleError = async (
		error,
		message = 'Your request could not be completed at this time. Please try again later.'
	) => {
		setAlertBanner({ visible: true, variant: 'error', text: error.body?.message ?? message });
		handleSetTimeout();
		throw error;
	};
	const handleSetTimeout = () => {
		if (timeoutId !== undefined) {
			window.clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(handleAlertClose, 10000);
	};
	const handleAlertClose = () => {
		setAlertBanner({ visible: false, variant: 'default', text: '' });
	};
	const handleRequestRejected = async () => {
		setRejectModalVisible(false);

		try {
			const cloudAccount = selectedCloudAccount;

			handleSubmitAlert('Submitting request...');

			const updatedAccount = cloudAccount.approver
				? await cloudAccountsService.rejectUserCloudAccount(cloudAccount.id, rejectionReason)
				: await cloudAccountsService.rejectCloudAccount(cloudAccount.id, rejectionReason);

			handleAccountChange(cloudAccount, updatedAccount);

			handleSuccessAlert(
				`The cloud-only account request '${cloudAccount.createdUserPrincipalName}' has been rejected.`
			);
		} catch (error) {
			handleError(error);
		} finally {
			setRejectionReason('');
		}
	};
	const handleAccountChange = (oldAccount: CloudAccount, newAccount: CloudAccount) => {
		const index = cloudAccounts.indexOf(oldAccount);
		const accounts = cloudAccounts;
		accounts[index] = newAccount;

		// update state and filter again to see changes reflected
		setCloudAccounts(accounts);
	};
	const handleSubmitAlert = (alertText: string) => {
		setAlertBanner({ visible: true, variant: 'default', text: alertText });
	};

	const handleSuccessAlert = (alertText: string) => {
		setAlertBanner({ visible: true, variant: 'success', text: alertText });
		handleSetTimeout();
	};
	const handleEndModal = (text: string, variant: AlertVariantType, timeout: number) => {
		setEndModal({
			visible: true,
			text,
			variant,
		});
		setTimeout(() => endAndRedirect(), timeout);
	};
	const endAndRedirect = () => {
		setEndModal({ visible: false });
		navigate('/');
	};
	useEffect(() => {
		setActiveTab(tab);
	}, []);

	useEffect(() => {
		retrieveApprovals(false);
	}, [refresh]);

	const listsDictionary = {
		accounts: cloudAccounts,
		apps: enterpriseApps,
		pors: PORs,
		dpis: DPIs,
		taps: TAPs,
		awspors: AWSPORs,
		onprempors: OnPremPORs,
	};
	const namesDictionary = {
		accounts: 'Cloud Accounts',
		apps: 'Entra ID Apps',
		pors: 'PORs',
		dpis: 'DPIs',
		taps: 'TAPs',
		awspors: 'AWS PORs',
		onprempors: 'On-Prem PORs',
	};

	const getFilterProps = () => {
		return {
			setActiveTab,
			retrieveApprovals,
			setFilteredDPIs,
			setFilteredAccounts,
			setFilteredTAPs,
			setFilteredPORs,
			setFilteredEnterpriseApps,
			setFilteredAWSPORs,
			setFilteredOnPremPORs,
			listsDictionary,
			namesDictionary,
			activeTab,
		};
	};

	return (
		<>
			<SectionHomeWrapper title="Approvals">
				<TextPassage>
					<p>
						<br />
					</p>
				</TextPassage>
				{alert.visible && (
					<Alert
						variant={alert.variant}
						body={alert.text}
						onClose={() => {
							setAlertBanner({
								visible: false,
							});
						}}
					/>
				)}
				{areMyApprovalsLoading && (
					<ProgressBar className="em-u-margin-top-half" indeterminate label={progressLabel} />
				)}
				{!areMyApprovalsLoading && (
					<>
						<ApprovalsHomeFilters {...getFilterProps()} />
						{loadingTabs[activeTab] ? (
						<ProgressBar className="em-u-margin-top-half" indeterminate label="Loading tab data..." />
						) : (
							{
								accounts: (
									<ApprovalsAccountsTable
										filteredAccounts={filteredAccounts}
										setApproveModalVisible={setApproveModalVisible}
										setReasonModalVisible={setReasonModalVisible}
										setRejectModalVisible={setRejectModalVisible}
										setSelectedCloudAccount={setSelectedCloudAccount}
									/>
								),
								apps: (
									<ApprovalsEntraIDAppsTable
										setAlert={setAlertBanner}
										filteredEnterpriseApps={filteredEnterpriseApps}
										setRefresh={setRefresh}
										refresh={refresh}
									/>
								),
								pors: <ApprovalsPORsTable filteredPORs={filteredPORs} />,
								dpis: <ApprovalsDPIsTable filteredDPIs={filteredDPIs} />,
								taps: (
									<ApprovalsTAPTable
										filteredTAPs={filteredTAPs}
										setEndModal={setEndModal}
										temporaryAccessPasswordService={temporaryAccessPasswordService}
									/>
								),
								awspors: <ApprovalsAWSPORsTable filteredAWSPORs={filteredAWSPORs} />,
								onprempors: <ApprovalsOnPremPORsTable filteredOnPremPORs={filteredOnPremPORs} />,
							}[activeTab]
						)}
					</>
				)}
				<ConfirmModal
					visible={approveModalVisible}
					title={`Approve the cloud-only account request '${selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`Are you sure you want to approve this request? 
							   A notification will be sent to ${selectedCloudAccount?.workflowInstance.createdBy.displayName} (${selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
					confirmButton={{ label: 'Confirm Approval' }}
					onConfirm={handleRequestApproved}
					onCancel={() => setApproveModalVisible(false)}
				/>
				<ConfirmModal
					visible={rejectModalVisible}
					title={`Reject the cloud-only account request '${selectedCloudAccount?.createdUserPrincipalName}'?`}
					question={`Are you sure you want to reject this request?
							   A notification will be sent to ${selectedCloudAccount?.workflowInstance.createdBy.displayName} (${selectedCloudAccount?.workflowInstance.createdBy.userPrincipalName.address}) 
							   upon confirmation informing them of this change.`}
					confirmButton={{ label: 'Confirm Rejection' }}
					confirmationField={{
						label: 'Please provide a reason for rejecting this request.',
						value: rejectionReason,
						validation: { required: true },
						onChange: (e) => setRejectionReason(e.target.value),
					}}
					onConfirm={handleRequestRejected}
					onCancel={() => {
						setRejectModalVisible(false);
						setRejectionReason('');
					}}
				/>
				<StandardModal
					show={reasonModalVisible}
					title={selectedCloudAccount?.createdUserPrincipalName}
					onDismiss={() => setReasonModalVisible(false)}
					footer={
						<Grid variant="2up">
							<Grid.Item>
								<Button variant="secondary" onClick={() => setReasonModalVisible(false)}>
									Close
								</Button>
							</Grid.Item>
						</Grid>
					}
				>
					{selectedCloudAccount?.workflowInstance.workflowInstanceStates.map((state) => (
						<TextArea
							key={state.workflowState.name}
							label={`${state.workflowState.name} on ${moment(state.performedOnDate).format('MM/DD/YYYY')} by ${state.performedByUser.displayName}`}
							state="em-is-disabled"
							value={`Reason: ${state.performedReason}`}
							onChange={() => {}}
						/>
					))}
				</StandardModal>
			</SectionHomeWrapper>
			<AlertModal {...endModal} willTimeout={false} onClose={endAndRedirect} />
		</>
	);
};
export default ApprovalsHome;
